import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import { RESERVATION_SETTINGS_MAPPING } from 'constants/ReservationMapping.constants';
import _ from 'lodash';
import type { TRootState } from 'slices/store';
import { createAppAsyncThunk } from 'slices/utils';
import type { IOption } from 'types/option.interface';
import type { IDefaultReducerState } from 'types/state.interface';
import type { WithOptional } from 'types/utils.type';
import {
  finishedLoadingFailure,
  finishedLoadingSuccess,
  isLoadingRequest,
} from 'utils/sliceHelpers';
import {
  EAppId,
  type IReservationMapping,
  type IReservationParamMapping,
  type ITypeMapping,
  createReservationMapping,
} from '@timeedit/types';
import api from '../services/api.service';

export type TReservationSettingState = IDefaultReducerState &
  IReservationMapping & {
    situations: IOption[];
    situationTypes: { [situation: string]: string[] };
    situationFields: { [situation: string]: IOption[] };
    objectExtTypes: IOption[];
    objectTypefields: IOption[];
  };

export type TReservationSetting = Pick<
  IReservationParamMapping,
  'appFieldId' | 'fieldExt'
>;

export const initialState: TReservationSettingState = {
  loading: false,
  hasErrors: false,
  appId: EAppId.TE_EXAM,
  paramMappings: [],
  situations: [],
  situationTypes: {},
  situationFields: {},
  objectExtTypes: [],
  objectTypefields: [],
};

// Slice
const slice = createSlice({
  name: 'reservationSettings',
  initialState,
  reducers: {
    fetchReservationMappingRequest: (state) => {
      isLoadingRequest(state);
    },
    fetchReservationMappingSuccess: (
      state,
      {
        payload,
      }: PayloadAction<WithOptional<IReservationMapping, 'paramMappings'>>
    ) => {
      const reservationSettings = createReservationMapping(payload);
      state.appId = reservationSettings.appId;
      state.paramMappings = reservationSettings.paramMappings;
      finishedLoadingSuccess(state);
    },
    fetchReservationMappingFailure: (state) => {
      finishedLoadingFailure(state);
    },
    saveReservationMappingRequest: (state) => {
      isLoadingRequest(state);
    },
    saveReservationMappingSuccess: (
      state,
      {
        payload,
      }: PayloadAction<WithOptional<IReservationMapping, 'paramMappings'>>
    ) => {
      const reservationSettings = createReservationMapping(payload);
      state.appId = reservationSettings.appId;
      state.paramMappings = reservationSettings.paramMappings;
      finishedLoadingSuccess(state);
    },
    saveReservationMappingFailure: (state) => {
      finishedLoadingFailure(state);
    },
    fetchReservationSituationsSuccess: (
      state,
      {
        payload,
      }: PayloadAction<
        Array<{
          extid: string;
          name: string;
        }>
      >
    ) => {
      state.situations = _.map(payload, ({ extid, name }) => ({
        label: name,
        value: extid,
      }));
      finishedLoadingSuccess(state);
    },
    fetchSituationFieldsSuccess: (
      state,
      {
        payload,
      }: PayloadAction<{
        situation: string;
        fields: string[];
      }>
    ) => {
      const { situation, fields } = payload;
      state.situationFields[situation] = _.map(fields, (field) => ({
        label: field,
        value: field,
      }));
      finishedLoadingSuccess(state);
    },
    fetchSituationTypesSuccess: (
      state,
      {
        payload,
      }: PayloadAction<{
        situation: string;
        types: string[];
      }>
    ) => {
      const { situation, types } = payload;
      state.situationTypes[situation] = types;
      finishedLoadingSuccess(state);
    },
    fetchObjectExtTypesSuccess: (
      state,
      {
        payload,
      }: PayloadAction<
        Array<{
          extid: string;
          name: string;
        }>
      >
    ) => {
      state.objectExtTypes = _.map(payload, ({ extid, name }) => ({
        label: name,
        value: extid,
      }));

      finishedLoadingSuccess(state);
    },
    fetchTypeFieldsSuccess: (
      state,
      { payload }: PayloadAction<TIntegrationObjectType[]>
    ) => {
      state.objectTypefields = _.map(payload, ({ fields }) => {
        const field = fields.te_extid;
        return {
          label: field,
          value: field,
        };
      });
      finishedLoadingSuccess(state);
    },
    updateReservationSettingsLocal: (
      state,
      { payload }: PayloadAction<TReservationSetting>
    ) => {
      const { appFieldId, fieldExt } = payload;
      const newSettings = [...state.paramMappings];
      const index = newSettings.findIndex(
        (item) => item.appFieldId === appFieldId
      );
      if (index > -1) {
        newSettings[index] = {
          appFieldId,
          fieldExt,
          fieldLabel: '',
        };
      } else {
        newSettings.push({
          appFieldId,
          fieldExt,
          fieldLabel: '',
        });
      }
      state.paramMappings = newSettings;
    },
  },
});

export default slice.reducer;

// Selectors
export const reservationSettingsLoading = (state: TRootState) =>
  state.reservationSettings.loading;
export const reservationMappingSelector = (state: TRootState) =>
  state.reservationSettings;
export const reservationSettingsSelector = (state: TRootState) =>
  state.reservationSettings.paramMappings;
export const situationsSelector = (state: TRootState) =>
  state.reservationSettings.situations;
export const situationTypesSelector = (state: TRootState) =>
  state.reservationSettings.situationTypes;

// TODO: Make a state of typeIdentified and use it in the selector
export const typeIdentifiedSelector = (
  state: TRootState
): Record<string, Record<string, boolean>> => {
  const mapping: ITypeMapping[] = _.get(
    state,
    'objectRelationships.objectMappings.objectTypes',
    []
  );
  const reservationSettings: IReservationParamMapping[] = _.get(
    state,
    'reservationSettings.paramMappings',
    []
  );
  const mappingByTypeName: Record<string, any> = mapping.reduce(
    (acc, cur) => ({
      ...acc,
      [cur.applicationObjectTypeName]: cur.objectTypeExtId,
    }),
    {}
  );
  const reservationSettingsByAppFieldId: Record<string, any> =
    reservationSettings.reduce(
      (acc, cur) => ({ ...acc, [cur.appFieldId]: cur.fieldExt }),
      {}
    );
  const situationTypes: Record<string, any> = _.get(
    state,
    'reservationSettings.situationTypes',
    {}
  );
  const identified: Record<string, Record<string, boolean>> = {};
  if (RESERVATION_SETTINGS_MAPPING[EAppId.TE_EXAM]) {
    RESERVATION_SETTINGS_MAPPING[EAppId.TE_EXAM].forEach(
      ({ modeFieldId, types }) => {
        if (modeFieldId && types) {
          const reservationTeplate =
            reservationSettingsByAppFieldId[modeFieldId] || '';
          const reservationTypes = situationTypes[reservationTeplate] || [];

          identified[modeFieldId] = Object.keys(types).reduce(
            (tot, typeKey) => {
              const reduxSource = _.get(
                types,
                [typeKey, 'reduxSource'],
                'reservationSettings'
              );
              const appType =
                reduxSource === 'reservationSettings'
                  ? reservationSettingsByAppFieldId[typeKey]
                  : mappingByTypeName[typeKey];

              return {
                ...tot,
                [typeKey]: !!appType && reservationTypes.includes(appType),
              };
            },
            {}
          );
        }
      }
    );
  }

  return identified;
};

export const objectExtTypesSelector = (state: TRootState) =>
  state.reservationSettings.objectExtTypes;

// Actions
export const {
  fetchReservationMappingRequest,
  fetchReservationMappingSuccess,
  fetchReservationMappingFailure,
  saveReservationMappingRequest,
  saveReservationMappingSuccess,
  saveReservationMappingFailure,
  fetchReservationSituationsSuccess,
  fetchSituationFieldsSuccess,
  fetchSituationTypesSuccess,
  fetchObjectExtTypesSuccess,
  fetchTypeFieldsSuccess,
  updateReservationSettingsLocal,
} = slice.actions;

export const fetchReservationMapping = createAppAsyncThunk(
  'reservationSettings/fetchReservationMapping',
  async (_, { dispatch }) => {
    try {
      dispatch(fetchReservationMappingRequest());
      const result = await api.get({
        endpoint: `/apps/${EAppId.TE_EXAM}/reservation-mapping`,
      });
      dispatch(fetchReservationMappingSuccess(result));
    } catch (e) {
      dispatch(fetchReservationMappingFailure());
      console.error(e);
    }
  }
);

export const saveReservationMapping = createAppAsyncThunk<
  void,
  IReservationParamMapping[]
>(
  'reservationSettings/saveReservationMapping',
  async (paramMappings, { dispatch }) => {
    try {
      dispatch(saveReservationMappingRequest());
      await api.patch({
        endpoint: `/apps/${EAppId.TE_EXAM}/reservation-mapping`,
        data: { paramMappings },
      });
      dispatch(
        saveReservationMappingSuccess({ appId: EAppId.TE_EXAM, paramMappings })
      );
    } catch (error) {
      dispatch(saveReservationMappingFailure());
      console.error(error);
    }
  }
);

export const fetchReservationSituations = createAppAsyncThunk(
  'reservationSettings/fetchReservationSituations',
  async (_, { dispatch }) => {
    try {
      const result = await api.get({
        endpoint: `/apps/${EAppId.TE_EXAM}/integration/reservations/situations`,
      });
      dispatch(fetchReservationSituationsSuccess(result.data || []));
    } catch (error) {
      console.error(error);
    }
  }
);

export const fetchSituationTypes = createAppAsyncThunk<void, string>(
  'reservationSettings/fetchSituationTypes',
  async (situation, { dispatch }) => {
    try {
      const result = await api.get({
        endpoint: `/apps/${EAppId.TE_EXAM}/integration/reservations/types/${situation}`,
      });
      dispatch(
        fetchSituationTypesSuccess({ situation, types: result.data || [] })
      );
    } catch (error) {
      console.error(error);
    }
  }
);

export const fetchSituationFields = createAppAsyncThunk<void, string>(
  'reservationSettings/fetchSituationFields',
  async (situation, { dispatch }) => {
    try {
      const result = await api.get({
        endpoint: `/apps/${EAppId.TE_EXAM}/integration/reservations/fields/${situation}`,
      });
      dispatch(
        fetchSituationFieldsSuccess({ situation, fields: result.data || [] })
      );
    } catch (error) {
      console.error(error);
    }
  }
);

export const fetchObjectExtTypes = createAppAsyncThunk(
  'reservationSettings/fetchObjectExtTypes',
  async (_, { dispatch }) => {
    try {
      const result = await api.get({
        endpoint: `/apps/${EAppId.TE_EXAM}/integration/objects/types`,
      });

      dispatch(fetchObjectExtTypesSuccess(result.data || []));
    } catch (error) {
      console.error(error);
    }
  }
);

type TIntegrationObjectType = {
  fields: TIntegrationObject;
};

type TIntegrationObject = {
  te_extid: string;
  [key: string]: string | number | undefined | null;
};

export const fetchTypeFields = createAppAsyncThunk<void, string>(
  'reservationSettings/fetchTypeFields',
  async (objectType, { dispatch }) => {
    try {
      const result = (await api.get({
        endpoint: `/apps/${EAppId.TE_EXAM}/integration/objects/types/${objectType}`,
      })) as { data: TIntegrationObjectType[] };
      dispatch(fetchTypeFieldsSuccess(result.data || []));
    } catch (error) {
      console.error(error);
    }
  }
);
